import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Column,
  Row,
  AlertBox,
  Spinner,
  PromoCode,
  Typography,
  FlexBox,
  AlertBoxDismissButton,
  Button,
  Link,
} from '@vp/swan';
import { useTranslations, ReplaceTokens } from '@vp/om-translations';
import CouponEntryForm from './CouponEntry';
import { pricingSelectors } from '../../state/pricing';
import useSiteData from '../../hooks/useSiteData';
import { DiscountModal } from '../pricing/DiscountModal';
import { CollapsibleHeader } from '../shared/CollapsibleHeader';
import { NegativeCouponWarning } from './NegativeCouponWarning';

/**
 * Container that toggles the CouponEntryForm visible/hidden, depending on a coupon being set.
 */
export const CouponPanel = () => {
  const ref = useRef(null);

  const [couponMessages, sharedActionMessages, cartMessages, lookUpFn] =
    useTranslations(['couponMessages', 'sharedActionMessages', 'cartMessages']);
  const { noCouponEntry, uiLocalization } = useSiteData();
  const siteLocale = uiLocalization?.locale;

  const coupon = useSelector(state =>
    pricingSelectors.selectDiscountCodeName(state.pricing)
  );
  const couponChangeInProgress = useSelector(state =>
    pricingSelectors.selectCouponChangeInProgress(state.pricing)
  );
  const couponErrorMessageKey = useSelector(state =>
    pricingSelectors.selectCouponErrorMessageKey(state.pricing)
  );
  const couponErrorSubstitutions = useSelector(state =>
    pricingSelectors.selectCouponErrorSubstitutions(state.pricing)
  );
  const discountSource = useSelector(state =>
    pricingSelectors.selectDiscountSource(state.pricing)
  );
  const hasCoupon = Boolean(coupon);
  const hasDiscountDetails = Boolean(
    discountSource?.id &&
      Object.keys(discountSource.details?.[siteLocale] ?? {}).length > 0
  );

  const hasError = Boolean(couponErrorMessageKey);

  const [couponFormShown, setCouponFormShown] = useState(false);
  const [couponShowError, setCouponShowError] = useState(hasError);
  const [discountModalOpen, setDiscountModalOpen] = useState(false);

  useEffect(() => {
    setCouponShowError(hasError);
    if (hasError) {
      setCouponFormShown(true);
    }
  }, [hasError]);

  useEffect(() => {
    if (couponShowError && ref.current) {
      ref.current.focus();
    }
  }, [ref, couponShowError]);

  if (noCouponEntry) {
    return null;
  }
  if (couponChangeInProgress) {
    return (
      <FlexBox justifyContent="center">
        <Spinner
          accessibleText={cartMessages.loading}
          aria-busy="true"
          aria-live="polite"
          role="status"
        />
      </FlexBox>
    );
  }
  return (
    <div data-testid="cart-coupon-panel">
      {couponShowError && (
        <AlertBox
          marginBottom={4}
          onRequestDismiss={() => setCouponShowError(false)}
        >
          <ReplaceTokens
            tokenizedString={
              lookUpFn('couponMessages', [couponErrorMessageKey]).hasAllCopy
                ? couponMessages[couponErrorMessageKey]
                : couponMessages.NotApplicable
            }
            substitutions={couponErrorSubstitutions}
          />
          <AlertBoxDismissButton
            visuallyHiddenLabel={sharedActionMessages.close}
            ref={ref}
          />
        </AlertBox>
      )}
      <Row mb={1}>
        <Column span={12}>
          <FlexBox justifyContent="space-between">
            <CollapsibleHeader
              text={
                hasCoupon
                  ? couponMessages.changeCode
                  : couponMessages.addPromoPrompt
              }
              fontSize={2}
              enabled
              onClick={() => setCouponFormShown(!couponFormShown)}
              isOpen={couponFormShown}
              contentId="couponPanelContents"
              testId="coupon-entry-collapsible-btn"
            />
            {hasCoupon && hasDiscountDetails && (
              <>
                <DiscountModal
                  discounts={{
                    itemDiscounts: [discountSource],
                  }}
                  selectedDiscountId={discountSource.promotionId}
                  open={discountModalOpen}
                  setIsOpen={setDiscountModalOpen}
                />
                <Link
                  render={p => (
                    <Button
                      skin="link"
                      className={p.className}
                      onClick={() => setDiscountModalOpen(true)}
                    >
                      {p.children}
                    </Button>
                  )}
                >
                  {couponMessages.offerDetails}
                </Link>
              </>
            )}
          </FlexBox>
        </Column>
      </Row>
      {!couponFormShown && hasCoupon && (
        <Row marginBottom={0}>
          <Column span={12}>
            <Typography component="span">
              <ReplaceTokens
                tokenizedString={couponMessages.promoCodeApplied}
                substitutions={{
                  promoCode: <PromoCode>{coupon}</PromoCode>,
                }}
              />
            </Typography>
          </Column>
        </Row>
      )}
      {couponFormShown && (
        <CouponEntryForm
          setEntryFormShown={val => setCouponFormShown(val)}
          className="couponEntryForm"
          classNameReason="testability"
        />
      )}
      <NegativeCouponWarning />
    </div>
  );
};
